import { useState } from 'react'
import { X, Plus, X as RemoveIcon, Eye, Send } from 'lucide-react'
import DOMPurify from 'dompurify'
import { useCreateTemplate, useUpdateTemplate, useSendTestEmail } from '@hooks/useTemplates'
import { useUIStore } from '@stores/ui'
import { Template } from '@/types'

interface TemplateEditorProps {
  template?: Template | null
  onClose: () => void
  onSuccess: () => void
}

interface FormData {
  name: string
  subject: string
  bodyHtml: string
  bodyText: string
  variables: string[]
}

export function TemplateEditor({ template, onClose, onSuccess }: TemplateEditorProps) {
  const [formData, setFormData] = useState<FormData>({
    name: template?.name || '',
    subject: template?.subject || '',
    bodyHtml: template?.bodyHtml || '',
    bodyText: template?.bodyText || '',
    variables: template?.variables || [],
  })
  const [newVariable, setNewVariable] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [showPreview, setShowPreview] = useState(false)
  const [showTestModal, setShowTestModal] = useState(false)

  const createMutation = useCreateTemplate()
  const updateMutation = useUpdateTemplate(template?.id || 0)

  const isEditMode = !!template

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validation
    const newErrors: Record<string, string> = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Template name is required'
    }

    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required'
    }

    if (!formData.bodyHtml.trim()) {
      newErrors.bodyHtml = 'HTML content is required'
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setErrors({})

    try {
      const payload = {
        name: formData.name.trim(),
        subject: formData.subject.trim(),
        bodyHtml: formData.bodyHtml.trim(),
        bodyText: formData.bodyText.trim() || undefined,
        variables: formData.variables.length > 0 ? formData.variables : undefined,
      }

      if (isEditMode) {
        await updateMutation.mutateAsync(payload)
      } else {
        await createMutation.mutateAsync(payload)
      }

      onSuccess()
      onClose()
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Failed to save template'
      const validationDetails = error.response?.data?.details

      if (validationDetails) {
        console.error('Validation errors:', validationDetails)
      }

      setErrors({ submit: errorMessage })
    }
  }

  const handleAddVariable = (variable: string) => {
    const trimmedVar = variable.trim()
    if (trimmedVar && !formData.variables.includes(trimmedVar)) {
      setFormData({ ...formData, variables: [...formData.variables, trimmedVar] })
      setNewVariable('')
    }
  }

  const handleRemoveVariable = (variable: string) => {
    setFormData({ ...formData, variables: formData.variables.filter((v) => v !== variable) })
  }

  const handleInsertVariable = (variable: string) => {
    const variableTag = `{{${variable}}}`
    // Insert at cursor position in bodyHtml
    setFormData({ ...formData, bodyHtml: formData.bodyHtml + variableTag })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="relative h-[90vh] w-full max-w-6xl overflow-hidden rounded-2xl bg-slate-900 border border-slate-700 shadow-2xl">
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-slate-700 px-6 py-4">
            <h2 className="text-xl font-semibold text-slate-100">
              {isEditMode ? 'Edit Template' : 'Create New Template'}
            </h2>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowPreview(true)}
                className="flex items-center gap-2 rounded-xl border border-slate-600 bg-slate-800 px-4 py-2 text-sm font-medium text-slate-200 hover:bg-slate-700 hover:text-white transition-colors"
              >
                <Eye size={16} />
                Preview
              </button>
              {template && (
                <button
                  type="button"
                  onClick={() => setShowTestModal(true)}
                  className="flex items-center gap-2 rounded-xl border border-slate-600 bg-slate-800 px-4 py-2 text-sm font-medium text-slate-200 hover:bg-slate-700 hover:text-white transition-colors"
                >
                  <Send size={16} />
                  Send Test
                </button>
              )}
              <button onClick={onClose} className="rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 bg-slate-900">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                {/* Template Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-slate-300">
                    Template Name <span className="text-rose-400">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className={`mt-1 w-full rounded-xl border ${
                      errors.name ? 'border-rose-500' : 'border-slate-600'
                    } bg-slate-800 px-3 py-2 text-slate-200 placeholder-slate-500 focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/20`}
                    placeholder="Welcome Email"
                  />
                  {errors.name && <p className="mt-1 text-sm text-rose-400">{errors.name}</p>}
                </div>

                {/* Subject */}
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-slate-300">
                    Subject Line <span className="text-rose-400">*</span>
                  </label>
                  <input
                    type="text"
                    id="subject"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    className={`mt-1 w-full rounded-xl border ${
                      errors.subject ? 'border-rose-500' : 'border-slate-600'
                    } bg-slate-800 px-3 py-2 text-slate-200 placeholder-slate-500 focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/20`}
                    placeholder="Welcome to our platform!"
                  />
                  {errors.subject && <p className="mt-1 text-sm text-rose-400">{errors.subject}</p>}
                </div>
              </div>

              {/* Variables */}
              <div>
                <label className="block text-sm font-medium text-slate-300">
                  Variables
                  <span className="ml-2 text-xs text-slate-500">
                    (Use these in your template as {`{{variable_name}}`})
                  </span>
                </label>

                {/* Variable List */}
                {formData.variables.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {formData.variables.map((variable) => (
                      <span
                        key={variable}
                        className="inline-flex items-center gap-1 rounded-full bg-cyan-500/15 px-3 py-1 text-xs font-semibold text-cyan-400 border border-cyan-500/20"
                      >
                        {`{{${variable}}}`}
                        <button
                          type="button"
                          onClick={() => handleInsertVariable(variable)}
                          className="hover:text-cyan-300 transition-colors"
                          title="Insert into template"
                        >
                          <Plus size={12} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveVariable(variable)}
                          className="hover:text-rose-400 transition-colors"
                          title="Remove variable"
                        >
                          <RemoveIcon size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Add Variable */}
                <div className="mt-2 flex gap-2">
                  <input
                    type="text"
                    value={newVariable}
                    onChange={(e) => setNewVariable(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        handleAddVariable(newVariable)
                      }
                    }}
                    placeholder="Add variable (e.g., first_name, company)"
                    className="flex-1 rounded-xl border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-slate-200 placeholder-slate-500 focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/20"
                  />
                  <button
                    type="button"
                    onClick={() => handleAddVariable(newVariable)}
                    className="flex items-center gap-1 rounded-xl bg-slate-700 px-3 py-2 text-sm font-medium text-slate-200 hover:bg-slate-600 transition-colors"
                  >
                    <Plus size={16} />
                    Add
                  </button>
                </div>
              </div>

              {/* HTML Content */}
              <div>
                <label htmlFor="bodyHtml" className="block text-sm font-medium text-slate-300">
                  HTML Content <span className="text-rose-400">*</span>
                </label>
                <textarea
                  id="bodyHtml"
                  value={formData.bodyHtml}
                  onChange={(e) => setFormData({ ...formData, bodyHtml: e.target.value })}
                  rows={15}
                  className={`mt-1 w-full rounded-xl border ${
                    errors.bodyHtml ? 'border-rose-500' : 'border-slate-600'
                  } bg-slate-800 px-3 py-2 font-mono text-sm text-slate-200 placeholder-slate-500 focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/20`}
                  placeholder="<html>&#10;<body>&#10;  <h1>Hello {{first_name}}!</h1>&#10;</body>&#10;</html>"
                />
                {errors.bodyHtml && <p className="mt-1 text-sm text-rose-400">{errors.bodyHtml}</p>}
                <p className="mt-1 text-xs text-slate-500">
                  Use HTML to format your email. Insert variables using {`{{variable_name}}`}
                </p>
              </div>

              {/* Text Content */}
              <div>
                <label htmlFor="bodyText" className="block text-sm font-medium text-slate-300">
                  Plain Text Version (optional)
                </label>
                <textarea
                  id="bodyText"
                  value={formData.bodyText}
                  onChange={(e) => setFormData({ ...formData, bodyText: e.target.value })}
                  rows={6}
                  className="mt-1 w-full rounded-xl border border-slate-600 bg-slate-800 px-3 py-2 font-mono text-sm text-slate-200 placeholder-slate-500 focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/20"
                  placeholder="Hello {{first_name}}!&#10;&#10;Plain text version for email clients that don't support HTML."
                />
                <p className="mt-1 text-xs text-slate-500">
                  Fallback for email clients that don't support HTML
                </p>
              </div>

              {/* Error Message */}
              {errors.submit && (
                <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-3">
                  <p className="text-sm text-rose-400">{errors.submit}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-3 border-t border-slate-700 pt-6">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-xl border border-slate-600 bg-slate-800 px-4 py-2 font-medium text-slate-300 hover:bg-slate-700 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="rounded-xl bg-cyan-600 px-4 py-2 font-medium text-white hover:bg-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? 'Saving...'
                    : isEditMode
                    ? 'Update Template'
                    : 'Create Template'}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Preview Modal */}
        {showPreview && (
          <PreviewModal
            htmlContent={formData.bodyHtml}
            onClose={() => setShowPreview(false)}
          />
        )}

        {/* Test Email Modal */}
        {showTestModal && template && (
          <TestEmailModal
            templateId={template.id}
            variables={formData.variables}
            onClose={() => setShowTestModal(false)}
          />
        )}
      </div>
    </div>
  )
}

function PreviewModal({ htmlContent, onClose }: { htmlContent: string; onClose: () => void }) {
  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-4xl rounded-2xl bg-slate-900 border border-slate-700 p-6 shadow-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-100">Template Preview</h3>
          <button onClick={onClose} className="rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>
        <div className="max-h-[60vh] overflow-auto rounded-xl border border-slate-700 bg-white p-6">
          <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(htmlContent, { USE_PROFILES: { html: true } }) }} />
        </div>
        <div className="mt-4 flex justify-end">
          <button
            onClick={onClose}
            className="rounded-xl bg-slate-700 px-4 py-2 font-medium text-slate-200 hover:bg-slate-600 transition-colors"
          >
            Close Preview
          </button>
        </div>
      </div>
    </div>
  )
}

function TestEmailModal({
  templateId,
  variables,
  onClose,
}: {
  templateId: number
  variables: string[]
  onClose: () => void
}) {
  const [email, setEmail] = useState('')
  const [variableValues, setVariableValues] = useState<Record<string, string>>({})
  const sendTestMutation = useSendTestEmail(templateId)
  const showToast = useUIStore((state) => state.showToast)

  const handleSend = async () => {
    if (!email) {
      showToast('Please enter an email address', 'warning')
      return
    }

    try {
      await sendTestMutation.mutateAsync({
        recipientEmail: email,
        variables: variableValues,
      })
      showToast('Test email sent successfully!', 'success')
      onClose()
    } catch {
      showToast('Failed to send test email', 'error')
    }
  }

  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-slate-900 border border-slate-700 p-6 shadow-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-100">Send Test Email</h3>
          <button onClick={onClose} className="rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300">Recipient Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full rounded-xl border border-slate-600 bg-slate-800 px-3 py-2 text-slate-200 placeholder-slate-500 focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/20"
              placeholder="test@example.com"
            />
          </div>

          {variables.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-slate-300">Variable Values</label>
              <div className="mt-2 space-y-2">
                {variables.map((variable) => (
                  <div key={variable}>
                    <label className="text-xs text-cyan-400">{`{{${variable}}}`}</label>
                    <input
                      type="text"
                      value={variableValues[variable] || ''}
                      onChange={(e) =>
                        setVariableValues({ ...variableValues, [variable]: e.target.value })
                      }
                      className="mt-1 w-full rounded-xl border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-slate-200 placeholder-slate-500 focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/20"
                      placeholder={`Enter ${variable}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 border-t border-slate-700 pt-4">
            <button
              onClick={onClose}
              className="rounded-xl border border-slate-600 bg-slate-800 px-4 py-2 text-sm font-medium text-slate-300 hover:bg-slate-700 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSend}
              disabled={sendTestMutation.isPending}
              className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {sendTestMutation.isPending ? 'Sending...' : 'Send Test'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
